import { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import fetchAuthToken from "../../../../utils/fetchAuthToken";
import {
  readOnlyRequestInterceptor,
  isBackendReadOnlyResponse,
} from "../../../../common/migration/readOnlyGuard";
import { notifyBlocked } from "../../../../common/migration/migrationStore";

// The insights service authenticates via the `firebase_id_token` cookie, which
// the dashboard sets with a short max-age (= Firebase token TTL, ~1h). Unlike
// Django's `__session` cookie, nothing rolls it forward on each request, so a
// user who keeps the dashboard open longer than the TTL loses the cookie and
// every subsequent report click 403s. These interceptors refresh the cookie
// before each request and recover from a server-rejected token once.

type RetryableConfig = AxiosRequestConfig & { authRetried?: boolean };

const attachAuthInterceptors = (client: AxiosInstance): void => {
  client.interceptors.request.use(async (config) => {
    await fetchAuthToken({});
    return config;
  });
  // Registered after the auth interceptor so it runs first (request interceptors
  // are LIFO). Report paths are allowlisted, so this is a no-op for them; it
  // exists so any future mutating call on this client is blocked consistently.
  client.interceptors.request.use(
    readOnlyRequestInterceptor as (c: AxiosRequestConfig) => AxiosRequestConfig
  );

  client.interceptors.response.use(undefined, async (error: AxiosError) => {
    // A read-only rejection must never be replayed by the auth-retry below.
    // NOTE: the report-generation client uses responseType: "blob", so on a
    // 403 the body is a Blob and its {code} can't be read synchronously here —
    // this detection only fires for JSON error bodies. That is acceptable: all
    // report paths are allowlisted (they are reads/exports), so the backend is
    // not expected to read-only-reject them; this branch is defence-in-depth.
    if (isBackendReadOnlyResponse(error)) {
      notifyBlocked();
      return Promise.reject(error);
    }
    const config = error.config as RetryableConfig | undefined;
    if (error.response?.status === 403 && config && !config.authRetried) {
      config.authRetried = true;
      await fetchAuthToken({ expire_current_token: true });
      return client.request(config);
    }
    return Promise.reject(error);
  });
};

export default attachAuthInterceptors;
